// src/pages/Admin/RosterManager.jsx
// Roster management for the admin panel.
//
// Written for a non-technical user: plain-English labels, one decision per row,
// a live preview of the card as it will appear on the site, and confirmation
// before anything destructive.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaPlus, FaTimes, FaTrash, FaPen, FaCamera, FaSpinner,
  FaCheckCircle, FaClock, FaEyeSlash, FaArrowLeft, FaDownload,
  FaChevronUp, FaChevronDown, FaCheck,
} from "react-icons/fa";
import {
  subscribeToAllPlayers, createPlayer, updatePlayer, removePlayer,
  seedFromStaticData, publishLabel, reorderDivision, effectiveAlsoAppearsIn,
  DIVISIONS, DIVISION_ORDER,
} from "../../lib/playersService";
import { uploadPlayerImage, formatBytes, displayableImageUrl } from "../../lib/imageUpload";
import { notifySaved, notifyRemoved } from "../../lib/notify";
import { subscribeToSectionSettings, updateSectionSettings } from "../../lib/siteSettings";
import { LEGACY_PLAYER_IMAGES, LEGACY_IMAGE_POSITIONS } from "../../data/legacyPlayerImages";

const SOCIAL_FIELDS = [
  { key: "tiktok", label: "TikTok" },
  { key: "twitch", label: "Twitch" },
  { key: "youtube", label: "YouTube" },
  { key: "twitter", label: "X (Twitter)" },
  { key: "instagram", label: "Instagram" },
];

const emptyForm = {
  slug: "", name: "", division: "CDL Team A", age: "", country: "",
  joined: "", specialization: "", achievements: [""], bio: "",
  socials: {}, youtubeUrl: "", imageUrl: "", imagePath: "", imagePosition: "",
  status: "draft", publishAt: null, sortOrder: 0, alsoAppearsIn: [],
  excludeFromNewMembers: false, currentTeam: "",
};

const input =
  "w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors";
const labelCls = "block text-sm font-semibold text-gray-300 mb-2";
const hintCls = "text-xs text-gray-500 mt-1.5";

// ─── Player card preview ─────────────────────────────────────────────────────
function CardPreview({ form, previewSrc }) {
  const imageKey = form.legacyImageKey || form.name;
  const src = previewSrc || displayableImageUrl(form.imageUrl, form.imagePath) || LEGACY_PLAYER_IMAGES[imageKey] || null;
  const pos = form.imagePosition || LEGACY_IMAGE_POSITIONS[imageKey] || "center top";
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden w-full max-w-[260px]">
      <div className="aspect-square overflow-hidden bg-gray-800">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" style={{ objectPosition: pos }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
            <span className="text-4xl font-black text-red-600/30">
              {(form.name || "?").charAt(0)}
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <span className="text-red-500 text-xs font-semibold uppercase tracking-wider">
          {form.division}
        </span>
        <h3 className="text-white font-bold text-lg mt-1">{form.name || "Player name"}</h3>
        <p className="text-gray-500 text-sm mt-1">{form.country || "Country"}</p>
        <p className="text-gray-600 text-xs mt-2 uppercase tracking-wide">
          {form.specialization || "What they play"}
        </p>
      </div>
    </div>
  );
}

// ─── Add / edit form ─────────────────────────────────────────────────────────
function PlayerForm({ initial, onDone, onCancel }) {
  // Seed cross-listings from the resolved value, not the raw field — otherwise
  // saving a player who's still on the original listings would wipe them.
  // publishAt comes out of Firestore as a Timestamp but the form (and toDoc)
  // work in milliseconds, so convert on the way in or editing a scheduled
  // player would corrupt their go-live time.
  const [form, setForm] = useState(() =>
    initial
      ? {
          ...initial,
          alsoAppearsIn: effectiveAlsoAppearsIn(initial),
          publishAt:
            typeof initial.publishAt?.toMillis === "function"
              ? initial.publishAt.toMillis()
              : initial.publishAt ?? null,
        }
      : emptyForm
  );
  const [previewSrc, setPreviewSrc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  // The admin list supplies raw Firestore documents, where the id sits on `id`;
  // records that came through usePlayers carry it as `docId`. Accepting either
  // matters because getting this wrong makes every edit save as a brand new
  // player instead of updating the existing one.
  const editingId = initial?.docId || initial?.id || null;
  const isEdit = Boolean(editingId);
  // Whether this player is currently on the site, as saved — not what the form
  // is currently set to. Drives the wording of the publish options.
  const isLive = initial?.status === "published";
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // Which of the three publish options is selected
  const publishMode = form.status !== "published" ? "draft" : form.publishAt ? "schedule" : "now";

  const setPublishMode = (mode) => {
    if (mode === "draft") setForm((f) => ({ ...f, status: "draft", publishAt: null }));
    if (mode === "now") setForm((f) => ({ ...f, status: "published", publishAt: null }));
    if (mode === "schedule") {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setForm((f) => ({ ...f, status: "published", publishAt: f.publishAt || tomorrow.getTime() }));
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setUploading(true); setUploadInfo(null);
    try {
      const localUrl = URL.createObjectURL(file);
      setPreviewSrc(localUrl);
      const result = await uploadPlayerImage(file, form.name || "player", { seed: Date.now() });
      setForm((f) => ({ ...f, imageUrl: result.url, imagePath: result.path }));
      setUploadInfo({ from: result.originalBytes, to: result.resizedBytes });
    } catch (err) {
      setError(err.message || "Upload failed. Please try again.");
      setPreviewSrc(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Please enter the player's name."); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...form, slug: form.slug || form.name.trim() };
      if (isEdit) await updatePlayer(editingId, payload);
      else await createPlayer(payload);
      // Confirmation to Discord — deliberately not awaited so a notification
      // problem can never block or fail the save.
      notifySaved(payload, { isEdit });
      onDone();
    } catch (err) {
      setError(err.message || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  const setAchievement = (i, value) =>
    setForm((f) => {
      const list = [...f.achievements];
      list[i] = value;
      return { ...f, achievements: list };
    });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm rounded-lg transition-colors">
          <FaArrowLeft size={12} /> Back
        </button>
        <h2 className="text-xl font-black uppercase tracking-wide text-white">
          {isEdit ? `Edit ${initial.name}` : "Add a player"}
        </h2>
      </div>

      {error && (
        <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">{error}</p>
      )}

      <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-start">
        {/* ── Form ── */}
        <div className="space-y-6">
          {/* Photo */}
          <div>
            <label className={labelCls}>Photo</label>
            <div
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                uploading ? "border-gray-700 cursor-wait" : "border-gray-700 hover:border-red-600/60 cursor-pointer"
              }`}
            >
              {uploading ? (
                <p className="text-gray-400 flex items-center justify-center gap-2">
                  <FaSpinner className="animate-spin" /> Uploading…
                </p>
              ) : (
                <>
                  <FaCamera className="mx-auto text-gray-600 mb-2" size={22} />
                  <p className="text-gray-300 text-sm font-semibold">
                    {form.imageUrl || previewSrc ? "Choose a different photo" : "Click to choose a photo"}
                  </p>
                  <p className={hintCls}>or drag one here · JPG or PNG</p>
                  <p className="text-xs text-gray-400 mt-2 bg-gray-800/70 inline-block px-3 py-1.5 rounded-lg">
                    Best format: <strong className="text-gray-200">square or upright (portrait)</strong>,
                    at least 800 × 800, with the player near the top. Very wide photos get their
                    sides cut off on the cards.
                  </p>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])} />
            {uploadInfo && (
              <p className="text-xs text-green-400 mt-2">
                Photo optimised for the web — {formatBytes(uploadInfo.from)} → {formatBytes(uploadInfo.to)}
              </p>
            )}
            {(form.imageUrl || previewSrc) && !uploading && (
              <button
                onClick={() => {
                  setPreviewSrc(null);
                  setUploadInfo(null);
                  setForm((f) => ({ ...f, imageUrl: "", imagePath: "" }));
                }}
                className="mt-2 text-xs text-gray-500 hover:text-red-400 transition-colors"
              >
                Remove this photo
              </button>
            )}
          </div>

          {/* Basics */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Name *</label>
              <input className={input} value={form.name} placeholder="e.g. Chozendontmiss"
                onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Section of the site</label>
              <select className={input} value={form.division} onChange={(e) => set("division", e.target.value)}>
                {DIVISIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            {form.division === "Graduates" && (
              <div className="sm:col-span-2">
                <label className={labelCls}>
                  Now playing for <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <input className={input} value={form.currentTeam || ""}
                  placeholder="e.g. Bush Team 2"
                  onChange={(e) => set("currentTeam", e.target.value)} />
                <p className={hintCls}>Shown on their Graduates card as "Now with …".</p>
              </div>
            )}
            <div>
              <label className={labelCls}>Country</label>
              <input className={input} value={form.country} placeholder="e.g. Jamaica"
                onChange={(e) => set("country", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Age</label>
              <input className={input} value={form.age} placeholder="e.g. 23"
                onChange={(e) => set("age", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>What they play</label>
              <input className={input} value={form.specialization} placeholder="e.g. Call of Duty || CDL"
                onChange={(e) => set("specialization", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Date joined</label>
              <input className={input} value={form.joined} placeholder="e.g. June 4, 2026"
                onChange={(e) => set("joined", e.target.value)} />
            </div>
          </div>

          {/* Cross-listing */}
          <div>
            <label className={labelCls}>
              Also show on <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <p className={`${hintCls} !mt-0 mb-3`}>
              Use this when someone belongs on a second page too — a Warzone player who
              also competes on a CDL team, for example. Leave everything unticked to show
              them only in their own section.
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {DIVISIONS.filter((d) => d.value !== form.division).map((d) => {
                const on = (form.alsoAppearsIn || []).includes(d.value);
                return (
                  <button
                    key={d.value}
                    onClick={() =>
                      setForm((f) => {
                        const current = f.alsoAppearsIn || [];
                        return {
                          ...f,
                          alsoAppearsIn: on
                            ? current.filter((v) => v !== d.value)
                            : [...current, d.value],
                        };
                      })
                    }
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                      on ? "border-red-600 bg-red-600/10" : "border-gray-700 hover:border-gray-600"
                    }`}
                  >
                    <span
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        on ? "bg-red-600 border-red-600" : "border-gray-600"
                      }`}
                    >
                      {on && <FaCheck size={8} className="text-white" />}
                    </span>
                    <span className="text-sm text-gray-200">{d.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bio */}
          <div>
            <label className={labelCls}>About them</label>
            <textarea className={`${input} min-h-[140px] resize-y`} value={form.bio}
              placeholder="A short write-up about the player…"
              onChange={(e) => set("bio", e.target.value)} />
          </div>

          {/* Achievements */}
          <div>
            <label className={labelCls}>Achievements</label>
            <div className="space-y-2">
              {form.achievements.map((a, i) => (
                <div key={i} className="flex gap-2">
                  <input className={input} value={a} placeholder="e.g. 2× LAN Champion"
                    onChange={(e) => setAchievement(i, e.target.value)} />
                  <button onClick={() => setForm((f) => ({ ...f, achievements: f.achievements.filter((_, j) => j !== i) }))}
                    className="px-3 text-gray-600 hover:text-red-500 transition-colors" aria-label="Remove">
                    <FaTimes />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => setForm((f) => ({ ...f, achievements: [...f.achievements, ""] }))}
              className="mt-2 text-sm text-red-400 hover:text-red-300 font-semibold flex items-center gap-1.5">
              <FaPlus size={10} /> Add another achievement
            </button>
          </div>

          {/* Video */}
          <div>
            <label className={labelCls}>Highlight video <span className="font-normal text-gray-500">(optional)</span></label>
            <input className={input} value={form.youtubeUrl} placeholder="Paste a YouTube link"
              onChange={(e) => set("youtubeUrl", e.target.value)} />
            <p className={hintCls}>Shows as a playable clip on their profile page.</p>
          </div>

          {/* Socials */}
          <div>
            <label className={labelCls}>Social links <span className="font-normal text-gray-500">(optional)</span></label>
            <div className="grid sm:grid-cols-2 gap-3">
              {SOCIAL_FIELDS.map(({ key, label }) => (
                <input key={key} className={input} placeholder={label}
                  value={form.socials?.[key] || ""}
                  onChange={(e) => setForm((f) => ({ ...f, socials: { ...f.socials, [key]: e.target.value } }))} />
              ))}
            </div>
          </div>

          {/* New members announcement */}
          <div>
            <button
              onClick={() => set("excludeFromNewMembers", !form.excludeFromNewMembers)}
              className="flex items-start gap-3 text-left"
            >
              <span
                className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  form.excludeFromNewMembers ? "bg-red-600 border-red-600" : "border-gray-600"
                }`}
              >
                {form.excludeFromNewMembers && <FaCheck size={10} className="text-white" />}
              </span>
              <span className="text-sm text-gray-300">
                Don't announce as a new member
                <span className="block text-xs text-gray-500 mt-0.5">
                  Tick this for someone who isn't really new — an existing player moving to
                  a different section, for example. New members otherwise appear on the
                  announcements page for a few days.
                </span>
              </span>
            </button>
          </div>

          {/* Publish */}
          <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-5">
            <label className={labelCls}>When should this go live?</label>
            <div className="space-y-2">
              {[
                // Wording shifts for someone already on the site — they're being
                // taken down, not drafted.
                isLive
                  ? { key: "draft", title: "Hide from the site", desc: "Keeps all their details, but removes them from the website" }
                  : { key: "draft", title: "Save as a draft", desc: "Not visible on the site yet" },
                { key: "now", title: isLive ? "Keep it live" : "Publish now", desc: "Visible on the site immediately" },
                { key: "schedule", title: "Schedule for later", desc: "Appears automatically on the date you pick" },
              ].map((opt) => (
                <button key={opt.key} onClick={() => setPublishMode(opt.key)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    publishMode === opt.key
                      ? "border-red-600 bg-red-600/10"
                      : "border-gray-700 hover:border-gray-600"
                  }`}>
                  <p className="text-white font-semibold text-sm">{opt.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
            {publishMode === "schedule" && (
              <div className="mt-3">
                <input type="datetime-local" className={input}
                  value={form.publishAt ? new Date(form.publishAt - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ""}
                  onChange={(e) => set("publishAt", e.target.value ? new Date(e.target.value).getTime() : null)} />
              </div>
            )}
          </div>

          {/* Save */}
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving || uploading}
              className="flex-1 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide rounded-lg transition-colors">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add player"}
            </button>
            <button onClick={onCancel} className="px-6 py-4 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>

        {/* ── Live preview ── */}
        <div className="lg:sticky lg:top-24">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">How it will look</p>
          <CardPreview form={form} previewSrc={previewSrc} />
        </div>
      </div>
    </div>
  );
}

// ─── Graduates page switch ───────────────────────────────────────────────────
// The Graduates section can be prepared here (add players under "Graduates")
// while the public page stays hidden, then unveiled with one tap.
function GraduatesToggle() {
  const [sections, setSections] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => subscribeToSectionSettings(setSections), []);
  if (!sections) return null;

  const isOn = !!sections.graduatesEnabled;

  const toggle = async () => {
    setSaving(true);
    setError("");
    try {
      await updateSectionSettings({ graduatesEnabled: !isOn });
    } catch (err) {
      setError(err.message || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <button
        onClick={toggle}
        disabled={saving}
        className={`w-full flex items-center justify-between gap-4 px-5 py-4 rounded-xl border-2 transition-colors text-left ${
          isOn ? "border-green-500/60 bg-green-500/10" : "border-gray-700 bg-gray-900 hover:border-gray-600"
        }`}
      >
        <div>
          <p className="text-white font-bold">
            {isOn ? "Graduates page is showing on the site" : "Graduates page is hidden"}
          </p>
          <p className="text-sm text-gray-400 mt-0.5">
            {isOn
              ? "Visitors can see it under Teams. Manage the players in the Graduates section below."
              : "Add players to the Graduates section below, then switch this on when you're ready to unveil it."}
          </p>
        </div>
        <span className={`flex-shrink-0 w-14 h-8 rounded-full p-1 transition-colors ${isOn ? "bg-green-500" : "bg-gray-700"}`}>
          <span className={`block w-6 h-6 bg-white rounded-full transition-transform ${isOn ? "translate-x-6" : "translate-x-0"}`} />
        </span>
      </button>
      {error && (
        <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 mt-2">{error}</p>
      )}
    </div>
  );
}

// ─── List ────────────────────────────────────────────────────────────────────
function RosterManager() {
  const [players, setPlayers] = useState(null);
  const [editing, setEditing] = useState(null); // player object or "new"
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeToAllPlayers(setPlayers), []);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 4000); };

  const handleImport = async () => {
    if (!window.confirm("Import the existing roster into the new system? This only needs doing once.")) return;
    setBusy(true);
    try {
      const count = await seedFromStaticData();
      flash(`Imported ${count} players.`);
    } catch (err) {
      flash(err.message);
    } finally { setBusy(false); }
  };

  const handleDelete = async (player) => {
    setBusy(true);
    try {
      await removePlayer(player);
      notifyRemoved(player);
      flash(`${player.name} removed.`);
      setConfirmDelete(null);
    } catch (err) { flash(err.message); }
    finally { setBusy(false); }
  };

  // Sections follow a fixed running order so the page doesn't reshuffle itself
  // when someone is added or moved. Anything with an unrecognised division
  // still gets shown, appended at the end, rather than silently disappearing.
  const grouped = useMemo(() => {
    const map = {};
    (players || []).forEach((p) => {
      const key = p.division || "Unassigned";
      (map[key] ||= []).push(p);
    });
    Object.values(map).forEach((list) =>
      list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    );
    const known = DIVISION_ORDER.filter((d) => map[d]?.length);
    const unknown = Object.keys(map).filter((d) => !DIVISION_ORDER.includes(d));
    return { map, order: [...known, ...unknown] };
  }, [players]);

  const handleMove = async (division, index, direction) => {
    const list = [...(grouped.map[division] || [])];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setBusy(true);
    try {
      await reorderDivision(list);
    } catch (err) {
      flash(err.message || "Could not save the new order.");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <PlayerForm
        initial={editing === "new" ? null : editing}
        onDone={() => { setEditing(null); flash("Saved."); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {notice && (
        <p className="text-sm bg-green-600/15 border border-green-600/40 text-green-300 rounded-lg px-4 py-3">{notice}</p>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black uppercase tracking-wide text-white">Roster</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Changes here go straight to the website. Use the arrows to change the order players appear in.
          </p>
        </div>
        <button onClick={() => setEditing("new")}
          className="flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 text-white font-bold uppercase text-sm rounded-lg transition-colors">
          <FaPlus size={12} /> Add a player
        </button>
      </div>

      <GraduatesToggle />

      {players === null && <p className="text-gray-500 animate-pulse">Loading roster…</p>}

      {players !== null && players.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-10 text-center space-y-4">
          <h3 className="text-white font-bold text-lg">Bring the current roster across</h3>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            The players already on the site need importing once. After that you can manage
            everyone from here.
          </p>
          <button onClick={handleImport} disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase rounded-lg transition-colors">
            <FaDownload size={13} /> {busy ? "Importing…" : "Import existing roster"}
          </button>
        </div>
      )}

      {grouped.order.map((division) => {
        const list = grouped.map[division] || [];
        return (
        <div key={division}>
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 border-l-2 border-red-600 pl-3">
            {division} · {list.length}
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {list.map((p, index) => {
              const status = publishLabel(p);
              const imageKey = p.legacyImageKey || p.name;
              const img = displayableImageUrl(p.imageUrl, p.imagePath) || LEGACY_PLAYER_IMAGES[imageKey];
              return (
                <div key={p.id} className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl p-3">
                  {/* Reorder within this division */}
                  {list.length > 1 && (
                    <div className="flex flex-col flex-shrink-0 -my-1">
                      <button
                        onClick={() => handleMove(division, index, -1)}
                        disabled={index === 0 || busy}
                        aria-label={`Move ${p.name} up`}
                        className="p-1 text-gray-600 hover:text-white disabled:opacity-25 disabled:hover:text-gray-600 transition-colors"
                      >
                        <FaChevronUp size={10} />
                      </button>
                      <button
                        onClick={() => handleMove(division, index, 1)}
                        disabled={index === list.length - 1 || busy}
                        aria-label={`Move ${p.name} down`}
                        className="p-1 text-gray-600 hover:text-white disabled:opacity-25 disabled:hover:text-gray-600 transition-colors"
                      >
                        <FaChevronDown size={10} />
                      </button>
                    </div>
                  )}
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                    {img ? (
                      <img src={img} alt="" className="w-full h-full object-cover"
                        style={{ objectPosition: p.imagePosition || LEGACY_IMAGE_POSITIONS[imageKey] || "center top" }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-red-600/40 font-black">
                        {p.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">{p.name}</p>
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold mt-0.5 ${
                      status === "Live" ? "text-green-400"
                        : status === "Draft" || status === "Hidden" ? "text-gray-500"
                        : "text-amber-400"
                    }`}>
                      {status === "Live" ? <FaCheckCircle size={9} />
                        : status === "Draft" || status === "Hidden" ? <FaEyeSlash size={9} />
                        : <FaClock size={9} />}
                      {status}
                    </span>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => setEditing(p)} className="p-2 text-gray-500 hover:text-white transition-colors" aria-label="Edit">
                      <FaPen size={12} />
                    </button>
                    <button onClick={() => setConfirmDelete(p)} className="p-2 text-gray-600 hover:text-red-500 transition-colors" aria-label="Remove">
                      <FaTrash size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg">Remove {confirmDelete.name}?</h3>
            <p className="text-gray-400 text-sm">
              They'll be taken off the site straight away. This can't be undone.
            </p>
            <div className="flex gap-2">
              <button onClick={() => handleDelete(confirmDelete)} disabled={busy}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase text-sm rounded-lg transition-colors">
                {busy ? "Removing…" : "Yes, remove"}
              </button>
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-bold uppercase text-sm rounded-lg transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RosterManager;
